import type { BrandReviewStatusFilter } from '@rock/db-admin';
import type { Brand, BrandReviewStatus } from '@rock/types';
import { BadgeAlert, BadgeCheck, BadgeMinus, BadgeQuestionMark, BadgeX, GitMerge, type LucideIcon } from 'lucide-react';

import type { badgeVariants } from '@/components/ui/badge';
import type { VariantProps } from 'class-variance-authority';

type BadgeVariant = VariantProps<typeof badgeVariants>['variant'];

/**
 * The brands list's status options, in display order. `'all'` is the unfiltered option; the query behind
 * it is `'live'` (no junk, no merged) unless the admin ticks "Include Junk / Merged", so `'live'` is a
 * query filter but never an option.
 */
export type BrandReviewStatusOption = Exclude<BrandReviewStatusFilter, 'live'>;
export const BRAND_REVIEW_STATUS_ORDER: readonly BrandReviewStatus[] = [
    'approved',
    'unreviewed',
    'no-name',
    'needs-attention',
    'junk',
];
export const BRAND_REVIEW_STATUS_FILTERS: readonly BrandReviewStatusOption[] = ['all', ...BRAND_REVIEW_STATUS_ORDER];
export type { BrandReviewStatusFilter };

interface BrandReviewStatusMeta {
    label: string;
    /** What the status means for matching, imagery and the review queue, for its tooltip. */
    hint: string;
    variant: BadgeVariant;
    className?: string;
    /** Small colored dot shown in the header dropdown next to the current status. */
    dotClassName: string;
    /** The status as a bare icon, for places with no room for the badge (explained by `hint`). */
    icon: LucideIcon;
    iconClassName: string;
}

export const BRAND_REVIEW_STATUS_META: Record<BrandReviewStatus, BrandReviewStatusMeta> = {
    approved: {
        label: 'Approved',
        hint: "Vouched for and promoted. Its logo shows in the app and it leads brand typeahead. A unit with no brand of its own prefers an approved brand's model, and its owner is asked to pick only when two or more approved brands share its number.",
        variant: 'secondary',
        className: 'bg-emerald-100 text-emerald-900 border-transparent',
        dotClassName: 'bg-emerald-500',
        icon: BadgeCheck,
        iconClassName: 'text-emerald-600',
    },
    unreviewed: {
        label: 'Unreviewed',
        hint: "New and not yet looked at. It matches equipment and recalls, but shows no logo in the app, isn't ranked ahead in typeahead, and waits in the review queue.",
        variant: 'outline',
        dotClassName: 'bg-slate-400',
        icon: BadgeQuestionMark,
        iconClassName: 'text-slate-400',
    },
    'no-name': {
        label: 'No-name',
        hint: "Reviewed, not promoted: a real brand nobody will curate, such as an import label. It stays out of the review queue and otherwise reads like an unreviewed brand: it matches equipment and recalls, but shows no logo, isn't ranked ahead in typeahead, and never makes an owner choose between it and another label on the same number.",
        variant: 'secondary',
        className: 'bg-slate-100 text-slate-700 border-transparent',
        dotClassName: 'bg-slate-500',
        icon: BadgeMinus,
        iconClassName: 'text-slate-500',
    },
    'needs-attention': {
        label: 'Needs attention',
        hint: 'Flagged for a closer look. It reads like an unreviewed brand until someone settles it: approved, no-name or junk.',
        variant: 'secondary',
        className: 'bg-amber-100 text-amber-900 border-transparent',
        dotClassName: 'bg-amber-500',
        icon: BadgeAlert,
        iconClassName: 'text-amber-600',
    },
    junk: {
        label: 'Junk',
        hint: 'Not a brand. Kept, not deleted. No name resolves to it, so nothing new links to it, and a model still linked to it reads as having no brand.',
        variant: 'destructive',
        dotClassName: 'bg-red-500',
        icon: BadgeX,
        iconClassName: 'text-red-600',
    },
};

/** A merged brand is stored as `junk` (the tombstone every reader skips) but reads as what it is. */
const BRAND_MERGED_META: BrandReviewStatusMeta = {
    label: 'Merged',
    hint: 'Merged into another brand, which now holds its names, models, recalls and documents. Kept as a pointer, so old links still resolve.',
    variant: 'secondary',
    className: 'bg-slate-100 text-slate-700 border-transparent',
    dotClassName: 'bg-slate-500',
    icon: GitMerge,
    iconClassName: 'text-slate-500',
};

/** The status meta a brand displays with: merged first, then the stored status. */
export function brandStatusMeta(brand: Pick<Brand, 'reviewStatus' | 'mergedInto'>): BrandReviewStatusMeta {
    return brand.mergedInto ? BRAND_MERGED_META : BRAND_REVIEW_STATUS_META[brand.reviewStatus];
}

/** The `junk` filter is the stored status, so it lists merged brands too. */
export const BRAND_REVIEW_STATUS_FILTER_LABELS: Record<BrandReviewStatusOption, string> = {
    all: 'All statuses',
    approved: 'Approved',
    unreviewed: 'Unreviewed',
    'no-name': 'No-name',
    'needs-attention': 'Needs attention',
    junk: 'Junk / Merged',
};
